import React, { useEffect, useState, useRef, useCallback } from "react"; // Added CSSProperties
import { CustomNodeData } from "./types";
import { HandleConfig } from "../../types";
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
// import AutoComplete from "../../components/shared/AutoComplete"; // Not needed for simple labels textarea
import HandleManager from "../../components/shared/HandleManager";
import IconSelector from "../../components/shared/IconSelector";
import ColorPicker from "../../components/shared/ColorPicker";

interface CustomConfigBoxProps {
  nodeId: string;
  data: CustomNodeData;
  onUpdate?: (data: Partial<CustomNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop for detaching
  isAdvancedMode?: boolean;
  isDisabled?: boolean;
  portalRoot?: HTMLElement | null;
}


export default function CustomConfigBox({
  nodeId,
  data,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure the new prop
  isAdvancedMode = false,
  isDisabled = false,
  portalRoot,
}: CustomConfigBoxProps) {
  // Initialize state with props
  const [name, setName] = useState(data.name || "");
  const [icon, setIcon] = useState(data.icon || "box"); // Default icon
  const [labelsText, setLabelsText] = useState(() => (Array.isArray(data.labels) ? data.labels.join('\n') : '')); // Labels as newline-separated text
  const [handles, setHandles] = useState<HandleConfig[]>(() => (Array.isArray(data.handles) ? [...data.handles] : []));
  const [colorOpacity, setColorOpacity] = useState(data.colorOpacity ?? 1); // Use data model opacity or default to 1

  // Initialize visibility settings
  const [nameVisibility, setNameVisibility] = useState<VisibilityOption>(
    data.nameVisibility === "hidden" ? "hidden" : "visible"
  );
  const [iconVisibility, setIconVisibility] = useState<VisibilityOption>(
    data.iconVisibility === "hidden" ? "hidden" : "visible"
  );
  const [labelsVisibility, setLabelsVisibility] = useState<VisibilityOption>( // Correctly define labelsVisibility state
    data.labelsVisibility === "hidden" ? "hidden" : "visible"
  );
  const [color, setColor] = useState<string | undefined>(data.color); // Use 'color' state

  const prevNodeIdRef = useRef<string | null>(null);
  const prevUpdateRef = useRef("");
  const isSyncingRef = useRef(false);

  // Sync state from props when node changes
  useEffect(() => {
    const currentNodeId = (data as any).id || JSON.stringify(data);

    if (prevNodeIdRef.current !== currentNodeId) {
      isSyncingRef.current = true; // Start sync

      setName(data.name || "");
      setIcon(data.icon || "box");
      setLabelsText(Array.isArray(data.labels) ? data.labels.join('\n') : '');
      setHandles(Array.isArray(data.handles) ? [...data.handles] : []);
      setNameVisibility(data.nameVisibility === "hidden" ? "hidden" : "visible");
      setIconVisibility(data.iconVisibility === "hidden" ? "hidden" : "visible");
      setLabelsVisibility(data.labelsVisibility === "hidden" ? "hidden" : "visible");
      setColor(data.color); // Sync color
      setColorOpacity(data.colorOpacity ?? 1); // Sync opacity

      prevNodeIdRef.current = currentNodeId;

      setTimeout(() => {
        isSyncingRef.current = false; // End sync
        prevUpdateRef.current = JSON.stringify({
          name: data.name || "",
          icon: data.icon || "box",
          labels: Array.isArray(data.labels) ? [...data.labels] : [],
          handles: Array.isArray(data.handles) ? [...data.handles] : [],
          nameVisibility: data.nameVisibility === "hidden" ? "hidden" : "visible",
          iconVisibility: data.iconVisibility === "hidden" ? "hidden" : "visible",
          labelsVisibility: data.labelsVisibility === "hidden" ? "hidden" : "visible",
          color: data.color, // Include color in initial sync ref
          colorOpacity: data.colorOpacity ?? 1, // Include opacity in initial sync ref
        });
      }, 0);
    } else {
      // Node ID is the same, but data reference might have changed.
      // Sync handles ONLY if the incoming data.handles is valid AND different from current state.
      // Node ID is the same, check individual props if needed
      if (Array.isArray(data.handles) && JSON.stringify(data.handles) !== JSON.stringify(handles)) {
        setHandles([...data.handles]);
      }
      // Sync background color if it differs
      if (data.color !== color) { // Check data.color against color state
        setColor(data.color); // Set color state
      }
      // Sync labels ONLY if incoming data.labels is valid AND different from current state.
      const incomingLabels = Array.isArray(data.labels) ? data.labels : [];
      // Compare incoming labels array with the current state derived from labelsText
      const currentLabelsFromText = labelsText.split('\n').filter(l => l); // Filter empty strings but don't trim
      if (JSON.stringify(incomingLabels) !== JSON.stringify(currentLabelsFromText)) {
        // Only update if the source data differs from the current text representation
        setLabelsText(incomingLabels.join('\n'));
      }
    }
  }, [data]); // Keep dependency primarily on data; internal state compared manually

  // Handler for HandleManager changes
  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    setHandles(newHandles);
  }, []);

  // Update parent when state changes
  useEffect(() => {
    if (isSyncingRef.current || !prevNodeIdRef.current || !onUpdate) return;

    // Parse labels from textarea - split by newline, filter empty lines, DO NOT trim lines
    const currentLabels = labelsText.split('\n').filter(l => l); // Keep spaces within labels

    const updates: Partial<CustomNodeData> = {
      name,
      icon,
      labels: currentLabels, // Use parsed labels
      handles,
      nameVisibility,
      iconVisibility,
      labelsVisibility,
      color, // Include color in updates
      colorOpacity, // Include opacity in updates
    };

    const updatesJson = JSON.stringify(updates);

    if (updatesJson !== prevUpdateRef.current) {
      // console.log("[CustomConfigBox] Change detected, calling onUpdate", updates);
      prevUpdateRef.current = updatesJson;
      onUpdate(updates);
    }
  }, [
    name,
    icon,
    labelsText, // Depend on the text area content
    handles,
    nameVisibility,
    iconVisibility,
    labelsVisibility,
    color, // Add color dependency
    colorOpacity, // Add opacity dependency
    onUpdate
  ]);

  // Toggle visibility setting
  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    setter(current === "hidden" ? "visible" : "hidden");
  };

  // Handle color change
  const handleColorChange = (colorValue: string | undefined) => {
    // When color is set to undefined (Default), we need to ensure it stays undefined
    // and doesn't get replaced with a default gray color
    setColor(colorValue);
  };
  
  // Handle opacity change
  const handleOpacityChange = (opacityValue: number) => {
    setColorOpacity(opacityValue);
  };

  // Common input styling
  const labelClasses = "block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1";

  return (
    <div className="space-y-3 relative">
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass the prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <LabelWithVisibilityToggle
          label="Node Name"
          visibility={nameVisibility}
          onToggleVisibility={() => toggleVisibility(nameVisibility, setNameVisibility)}
          className="mb-1 mt-5"
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Node name"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
          disabled={isDisabled}
        />
      </div>

      {/* Labels Input */}
      <div>
        <LabelWithVisibilityToggle
          label="Labels (one per line)"
          visibility={labelsVisibility}
          onToggleVisibility={() => toggleVisibility(labelsVisibility, setLabelsVisibility)}
          className="mb-1 mt-3"
        />
        <textarea
          value={labelsText}
          onChange={(e) => setLabelsText(e.target.value)}
          placeholder="Label 1&#10;Label 2&#10;Label 3"
          rows={3}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
          disabled={isDisabled}
        />
      </div>

      {/* Color Picker */}
      <div>
        <label className={labelClasses}>Background Color</label>
        <ColorPicker
          value={color}
          onChange={handleColorChange}
          opacity={colorOpacity}
          onOpacityChange={handleOpacityChange}
          portalRoot={document.body}
          disabled={isDisabled}
          showOpacitySlider={true}
        />
      </div>

      {/* Icon Selection */}
      <div>
        <LabelWithVisibilityToggle
          label="Icon"
          visibility={iconVisibility}
          onToggleVisibility={() => toggleVisibility(iconVisibility, setIconVisibility)}
          className="mb-1 mt-3"
        />
        <IconSelector
          value={icon}
          onChange={setIcon}
          portalRoot={portalRoot}
          disabled={isDisabled}
        />
      </div>

      {/* Handle Manager */}
      <div className="pt-1 mt-1">
        <HandleManager
          nodeId={nodeId}
          handles={handles}
          onChange={handleHandlesChange}
          maxHandles={8} // Allow more handles if needed
          portalRoot={portalRoot}
        />
      </div>
    </div>
  );
}
